import { lazy, type ComponentType, type LazyExoticComponent } from "react";
import type { AdminPageId } from "@/app/navigation";
import { createPagePrefetcher } from "@/app/pagePrefetch";

export type PageComponent = LazyExoticComponent<ComponentType>;

export const PAGE_MODULE_PATHS = {
  "command-center": "MediaContentCommandCenter",
  "team-operations": "TeamOperationsAssignmentCenter",
  "listing-intake": "ListingIntakeQueue",
  "listing-detail": "ListingDetailPage",
  "livedealz-intake": "LiveDealzAssetIntakeGovernance",
  "image-builder": "ImageBuilderWorkbench",
  "video-slideshow-builder": "VideoSlideshowBuilderWorkbench",
  "content-workbench": "ContentAITranslationComparisonWorkbench",
  "media-review": "ListingMediaReviewBoard",
  "final-listing-approval": "FinalListingApprovalWorkspace",
  "asset-library-builder": "AssetLibraryBuilder",
  "livedealz-review": "LiveDealzAssetReviewBoard",
  "user-asset-libraries": "UserAssetLibrariesApprovedPackageLibrary",
  automation: "AutomationPromptIntegrationCenter",
  "analytics-audit": "AnalyticsSLAAudit",
  "roles-permissions": "RolesPermissionsCenter",
  "suppliers-providers": "SupplierProviderDirectoryOnboarding",
  campaigns: "CampaignManagementHub",
  "dealz-control": "DealzLiveDealzControlRoom",
  inbox: "NotificationsTasksAccessRequestsInbox",
  "platform-settings": "PlatformSettingsTaxonomiesWorkflowPolicies",
} satisfies Partial<Record<AdminPageId, string>>;

export const PAGE_COMPONENTS: Partial<Record<AdminPageId, PageComponent>> = {
  "command-center": lazy(() => import("@/pages/MediaContentCommandCenter")),
  "team-operations": lazy(() => import("@/pages/TeamOperationsAssignmentCenter")),
  "listing-intake": lazy(() => import("@/pages/ListingIntakeQueue")),
  "listing-detail": lazy(() => import("@/pages/ListingDetailPage")),
  "livedealz-intake": lazy(() => import("@/pages/LiveDealzAssetIntakeGovernance")),
  "image-builder": lazy(() => import("@/pages/ImageBuilderWorkbench")),
  "video-slideshow-builder": lazy(() => import("@/pages/VideoSlideshowBuilderWorkbench")),
  "content-workbench": lazy(() => import("@/pages/ContentAITranslationComparisonWorkbench")),
  "media-review": lazy(() => import("@/pages/ListingMediaReviewBoard")),
  "final-listing-approval": lazy(() => import("@/pages/FinalListingApprovalWorkspace")),
  "asset-library-builder": lazy(() => import("@/pages/AssetLibraryBuilder")),
  "livedealz-review": lazy(() => import("@/pages/LiveDealzAssetReviewBoard")),
  "user-asset-libraries": lazy(() => import("@/pages/UserAssetLibrariesApprovedPackageLibrary")),
  automation: lazy(() => import("@/pages/AutomationPromptIntegrationCenter")),
  "analytics-audit": lazy(() => import("@/pages/AnalyticsSLAAudit")),
  "roles-permissions": lazy(() => import("@/pages/RolesPermissionsCenter")),
  "suppliers-providers": lazy(() => import("@/pages/SupplierProviderDirectoryOnboarding")),
  campaigns: lazy(() => import("@/pages/CampaignManagementHub")),
  "dealz-control": lazy(() => import("@/pages/DealzLiveDealzControlRoom")),
  inbox: lazy(() => import("@/pages/NotificationsTasksAccessRequestsInbox")),
  "platform-settings": lazy(() => import("@/pages/PlatformSettingsTaxonomiesWorkflowPolicies")),
};

const prefetch = createPagePrefetcher();

const PAGE_IMPORTERS: Record<string, () => Promise<unknown>> = {
  MediaContentCommandCenter: () => import("@/pages/MediaContentCommandCenter"),
  TeamOperationsAssignmentCenter: () => import("@/pages/TeamOperationsAssignmentCenter"),
  ListingIntakeQueue: () => import("@/pages/ListingIntakeQueue"),
  ListingDetailPage: () => import("@/pages/ListingDetailPage"),
  LiveDealzAssetIntakeGovernance: () => import("@/pages/LiveDealzAssetIntakeGovernance"),
  ImageBuilderWorkbench: () => import("@/pages/ImageBuilderWorkbench"),
  VideoSlideshowBuilderWorkbench: () => import("@/pages/VideoSlideshowBuilderWorkbench"),
  ContentAITranslationComparisonWorkbench: () => import("@/pages/ContentAITranslationComparisonWorkbench"),
  ListingMediaReviewBoard: () => import("@/pages/ListingMediaReviewBoard"),
  FinalListingApprovalWorkspace: () => import("@/pages/FinalListingApprovalWorkspace"),
  AssetLibraryBuilder: () => import("@/pages/AssetLibraryBuilder"),
  LiveDealzAssetReviewBoard: () => import("@/pages/LiveDealzAssetReviewBoard"),
  UserAssetLibrariesApprovedPackageLibrary: () => import("@/pages/UserAssetLibrariesApprovedPackageLibrary"),
  AutomationPromptIntegrationCenter: () => import("@/pages/AutomationPromptIntegrationCenter"),
  AnalyticsSLAAudit: () => import("@/pages/AnalyticsSLAAudit"),
  RolesPermissionsCenter: () => import("@/pages/RolesPermissionsCenter"),
  SupplierProviderDirectoryOnboarding: () => import("@/pages/SupplierProviderDirectoryOnboarding"),
  CampaignManagementHub: () => import("@/pages/CampaignManagementHub"),
  DealzLiveDealzControlRoom: () => import("@/pages/DealzLiveDealzControlRoom"),
  NotificationsTasksAccessRequestsInbox: () => import("@/pages/NotificationsTasksAccessRequestsInbox"),
  PlatformSettingsTaxonomiesWorkflowPolicies: () => import("@/pages/PlatformSettingsTaxonomiesWorkflowPolicies"),
};

/**
 * Eagerly fetch a page chunk so subsequent navigation feels instant.
 * Safe to call repeatedly; each page is only prefetched once.
 */
export function prefetchPage(id: AdminPageId): void {
  const path = PAGE_MODULE_PATHS[id as keyof typeof PAGE_MODULE_PATHS];
  if (!path) return;
  const importer = PAGE_IMPORTERS[path];
  if (!importer) return;
  prefetch(id, importer);
}

/**
 * Prefetch a list of pages. Defaults to the most commonly visited workspace pages.
 */
export function prefetchCommonPages(ids: AdminPageId[] = ["command-center", "listing-intake", "livedealz-intake", "user-asset-libraries"]): void {
  for (const id of ids) {
    prefetchPage(id);
  }
}
